import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Rocket, Zap, LucideIcon } from "lucide-react";
import Container from "../global/container";
import { Button } from "../ui/button";
import Particles from "../ui/particles";
import { cn } from "@/lib";

interface CTAAction {
  text: string;
  href: string;
  variant?: "default" | "outline" | "secondary";
  icon?: LucideIcon;
}

interface CTABadge {
  icon: LucideIcon;
  text: string;
}

interface CTASectionProps {
  title?: string;
  subtitle?: string;
  badge?: CTABadge;
  actions?: CTAAction[];
  disclaimer?: string;
  variant?: "default" | "gradient" | "simple";
  className?: string;
  showParticles?: boolean;
  showFloatingElements?: boolean;
}

const CTASection = ({
  title = "Ready to revolutionize your marketing strategy?",
  subtitle = "Transform your marketing with AI-powered automation. Create campaigns faster, generate better content and make smarter decisions in minutes.",
  badge = { icon: Zap, text: "Boost your ROI by up to 300%" },
  actions = [
    {
      text: "Get started now",
      href: "/demo",
      variant: "default",
      icon: ArrowUpRight,
    },
    { text: "Schedule a demo", href: "/demo", variant: "outline" },
  ],

  disclaimer = "No credit card required • 14-day free trial",
  variant = "default",
  className,
  showParticles = true,
  showFloatingElements = true,
}: CTASectionProps) => {
  const BadgeIcon = badge.icon;

  const getBackgroundClasses = () => {
    switch (variant) {
      case "gradient":
        return "bg-gradient-to-r from-blue-600 to-purple-600 text-white";
      case "simple":
        return "bg-card border border-border";
      default:
        return "bg-gradient-to-br from-background/80 via-background/60 to-background/40 backdrop-blur-sm border border-foreground/10";
    }
  };

  const getTextClasses = () => {
    switch (variant) {
      case "gradient":
        return "text-white";
      default:
        return "text-foreground";
    }
  };

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center w-full py-20",
        className,
      )}
      data-oid="67xlz_3"
    >
      <Container className="py-20 max-w-6xl mx-auto" data-oid="3c7vxul">
        <div
          className={cn(
            "relative flex flex-col items-center justify-center py-12 lg:py-24 px-6 lg:px-12 rounded-[2.5rem] text-center shadow-xl overflow-hidden",
            getBackgroundClasses(),
          )}
          data-oid="4u-4b-s"
        >
          {/* Interactive particles background */}
          {showParticles && variant !== "gradient" && (
            <>
              <Particles
                refresh
                ease={100}
                quantity={100}
                color="#d4d4d8"
                className="hidden lg:block absolute inset-0 z-0"
                data-oid="slli.6r"
              />

              <Particles
                refresh
                ease={80}
                quantity={40}
                color="#d4d4d8"
                className="block lg:hidden absolute inset-0 z-0"
                data-oid="jxair59"
              />
            </>
          )}

          {/* Animated background gradient */}
          {variant === "default" && (
            <motion.div
              className="absolute -bottom-1/4 left-1/3 -translate-x-1/2 w-64 h-48 lg:h-72 lg:w-1/2 rounded-full blur-[6rem] lg:blur-[12rem] -z-10"
              style={{
                background:
                  "conic-gradient(from 30deg at 50% 50%, #a855f7 0deg, #3b82f6 120deg, #06b6d4 240deg, #8b5cf6 360deg)",
              }}
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: {
                  duration: 15,
                  repeat: Infinity,
                  ease: "linear",
                },
                scale: {
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              data-oid="_wzzfc2"
            />
          )}

          {/* Floating decoration elements */}
          {showFloatingElements && (
            <>
              <motion.div
                className="absolute top-12 right-16 hidden lg:block"
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 5, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                data-oid="7a2a.46"
              >
                <Rocket
                  className={cn(
                    "h-10 w-10",
                    variant === "gradient"
                      ? "text-white/70"
                      : "text-blue-500/70",
                  )}
                  data-oid="987:34v"
                />
              </motion.div>

              <motion.div
                className="absolute bottom-16 left-16 hidden lg:block"
                animate={{
                  y: [0, 10, 0],
                  rotate: [0, -5, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                data-oid="o-luigw"
              >
                <Sparkles
                  className={cn(
                    "h-8 w-8",
                    variant === "gradient"
                      ? "text-white/70"
                      : "text-purple-500/70",
                  )}
                  data-oid=":h6x7r3"
                />
              </motion.div>
            </>
          )}

          {/* Main content with animated reveal */}
          <motion.div
            className="relative z-10 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-oid="-boij-7"
          >
            {/* Badge */}
            <div className="mb-6 flex justify-center" data-oid="7feg18x">
              <motion.div
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-full border",
                  variant === "gradient"
                    ? "bg-white/10 border-white/20"
                    : "bg-foreground/5 border-foreground/10",
                )}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
                data-oid="-rrk5y-"
              >
                <BadgeIcon
                  className={cn(
                    "h-4 w-4",
                    variant === "gradient" ? "text-white" : "text-blue-500",
                  )}
                  data-oid="cnzkhe2"
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    variant === "gradient"
                      ? "text-white"
                      : "text-foreground/80",
                  )}
                  data-oid="qw1vce6"
                >
                  {badge.text}
                </span>
              </motion.div>
            </div>

            <h2
              className={cn(
                "text-3xl md:text-5xl lg:text-6xl font-heading font-bold !leading-tight",
                variant === "gradient"
                  ? "text-white"
                  : "bg-clip-text text-transparent bg-gradient-to-r from-foreground via-foreground/90 to-foreground/80",
              )}
              data-oid="gz5_p19"
            >
              {title}
            </h2>

            <p
              className={cn(
                "text-sm md:text-lg text-center max-w-2xl mx-auto mt-6",
                variant === "gradient"
                  ? "text-white/90"
                  : "text-accent-foreground/80",
              )}
              data-oid="cr0gxrp"
            >
              {subtitle}
            </p>

            <div
              className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4"
              data-oid="9f9uk64"
            >
              {actions.map((action, index) => {
                const ActionIcon = action.icon;
                return (
                  <Link
                    key={index}
                    to={action.href}
                    className="w-full sm:w-auto"
                    data-oid="2ctnlse"
                  >
                    <Button
                      size="lg"
                      variant={action.variant || "default"}
                      className={cn(
                        "w-full sm:w-auto px-8 py-6 text-base rounded-full transition-all duration-300",
                        action.variant === "default" &&
                          "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30",
                        action.variant === "outline" &&
                          variant === "gradient" &&
                          "border-white text-white hover:bg-white/10",
                        action.variant === "outline" &&
                          variant !== "gradient" &&
                          "border-foreground/20 hover:bg-foreground/5",
                      )}
                      data-oid="1axhhu0"
                    >
                      <span data-oid="f67cyt6">{action.text}</span>
                      {ActionIcon && (
                        <ActionIcon
                          className="ml-2 h-5 w-5"
                          data-oid="lvk65ct"
                        />
                      )}
                    </Button>
                  </Link>
                );
              })}
            </div>

            {disclaimer && (
              <p
                className={cn(
                  "mt-6 text-sm",
                  variant === "gradient"
                    ? "text-white/70"
                    : "text-foreground/60",
                )}
                data-oid="5wggz-p"
              >
                <span
                  className={cn(
                    "px-2 py-1 rounded",
                    variant === "gradient" ? "bg-white/10" : "bg-foreground/5",
                  )}
                  data-oid="ypsh7p_"
                >
                  {disclaimer.split(" • ")[0]}
                </span>
                {disclaimer.includes(" • ") && (
                  <>
                    {" • "}
                    <span className="font-medium" data-oid="n5uc40g">
                      {disclaimer.split(" • ")[1]}
                    </span>
                  </>
                )}
              </p>
            )}
          </motion.div>
        </div>
      </Container>
    </div>
  );
};

export default CTASection;
